import React, { useEffect, useState, useRef } from 'react';
import { Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody, Box, CircularProgress, Button, useTheme, useMediaQuery } from '@mui/material';
import ChamadoModal from '../emer_socorrista/ChamadoModal';
import { supabase } from '../../Supabase/supabaseRealtimeClient';
import { useNavigate } from 'react-router-dom';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';

export interface Chamado {
  id: string;
  cliente_id: string;
  status: string;
  data_abertura: string;
  localizacao: string;
  endereco_textual?: string;
  posicao_inicial_socorrista?: string;
  operador_id?: string;
  data_fechamento?: string;
  descricao?: string;
  prioridade?: string;
  notificacao_familiares?: any[];
}

// Função para ativar o sino no Navbar
const ativarSinoNotificacao = () => {
  const event = new CustomEvent('novaNotificacao');
  window.dispatchEvent(event);
  
  // Salvar no localStorage que há uma notificação não visualizada
  localStorage.setItem('visualizado', 'false');
};

// Função para desativar o sino no Navbar
const desativarSinoNotificacao = () => {
  const event = new CustomEvent('desativarNotificacao');
  window.dispatchEvent(event);
};

// Função para obter headers de autenticação
const getAuthHeaders = () => {
  const serviceKey = process.env.REACT_APP_SUPABASE_SERVICE_KEY;
  const accessToken = localStorage.getItem('accessToken');
  
  if (!serviceKey) throw new Error('Supabase Service Key não definido');
  if (!accessToken) throw new Error('accessToken não encontrado');
  
  return {
    'apikey': serviceKey,
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };
};

// Função para validar ambiente
const validateEnvironment = () => {
  const url = process.env.REACT_APP_SUPABASE_URL;
  const familiarId = localStorage.getItem('familiarId');
  
  if (!url) throw new Error('Supabase URL não definida');
  if (!familiarId) throw new Error('familiarId não encontrado');
  
  return { url, familiarId };
};

// Função para verificar se a mudança é relevante para o familiar
const isRelevantChange = async (payload: any) => {
  const familiarId = localStorage.getItem('familiarId');
  if (!familiarId) return false;

  // Verificar se o chamado tem notificacao_familiares
  const notificacaoFamiliares = payload.new?.notificacao_familiares || payload.old?.notificacao_familiares;
  
  // Se tem notificacao_familiares, verificar se o familiar está na lista
  if (notificacaoFamiliares && Array.isArray(notificacaoFamiliares)) {
    const isRelevant = notificacaoFamiliares.some((nf: any) => nf.id === familiarId);
    
    console.log('Verificando relevância (com notificacao_familiares):', {
      familiarId,
      notificacaoFamiliares,
      isRelevant,
      eventType: payload.eventType
    });
    
    return isRelevant;
  }

  // Se não tem notificacao_familiares, verificar se o chamado pertence ao cliente do familiar
  try {
    const clienteId = localStorage.getItem('clienteId');
    if (!clienteId) return false;

    const chamadoClienteId = payload.new?.cliente_id || payload.old?.cliente_id;
    
    // Se o chamado é do mesmo cliente do familiar, é relevante
    if (chamadoClienteId && chamadoClienteId.toString() === clienteId) {
      console.log('Verificando relevância (por cliente_id):', {
        familiarId,
        clienteId,
        chamadoClienteId,
        isRelevant: true,
        eventType: payload.eventType
      });
      return true;
    }
  } catch (error) {
    console.error('Erro ao verificar relevância por cliente_id:', error);
  }
  
  console.log('Mudança não relevante:', {
    familiarId,
    notificacaoFamiliares,
    eventType: payload.eventType
  });
  
  return false;
};

// Função para salvar dados no localStorage e Capacitor Preferences
const saveToStorage = async (key: string, value: string) => {
  localStorage.setItem(key, value);
  if (Capacitor.isNativePlatform()) {
    try {
      await Preferences.set({ key, value });
      console.log(`[NotificaoList] ${key} salvo no Capacitor Preferences`);
    } catch (error) {
      console.error(`[NotificaoList] Erro ao salvar ${key} no Preferences:`, error);
    }
  }
};

const NotificaoList: React.FC = () => {
  const [chamados, setChamados] = useState<Chamado[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChamado, setSelectedChamado] = useState<Chamado | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const previousChamadosRef = useRef<string[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const notificationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Função para tocar a notificação
  const tocarNotificacao = () => {
    if (audioRef.current) {
      audioRef.current.play().catch(err => {
        // Ignorar erro silenciosamente - navegadores modernos bloqueiam autoplay
        console.log('Áudio bloqueado pelo navegador - requer interação do usuário');
      });
    }
  };

  // Função para iniciar o loop de notificação
  const iniciarLoopNotificacao = () => {
    // Limpar intervalo anterior se existir
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current);
    }

    // Tocar a primeira notificação imediatamente
    tocarNotificacao();

    // Configurar intervalo para tocar a cada 3 segundos
    notificationIntervalRef.current = setInterval(() => {
      const visualizado = localStorage.getItem('visualizado');
      
      if (visualizado === 'true') {
        // Parar o loop se foi visualizado
        if (notificationIntervalRef.current) {
          clearInterval(notificationIntervalRef.current);
          notificationIntervalRef.current = null;
        }
      } else {
        // Continuar tocando se ainda não foi visualizado
        tocarNotificacao();
      }
    }, 3000); // 3 segundos
  };

  // Função para parar o loop de notificação
  const pararLoopNotificacao = () => {
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current);
      notificationIntervalRef.current = null;
    }
  };

  const handleVisualizar = async (chamado: Chamado) => {
    // Desativar o sino quando visualizar o chamado
    desativarSinoNotificacao();
    
    // Marcar como visualizado no localStorage
    localStorage.setItem('visualizado', 'true');
    
    // Parar o loop de notificação
    pararLoopNotificacao();
    
    await saveToStorage('chamadoFamilia', JSON.stringify(chamado));
    await saveToStorage('chamadoId', chamado.id);
    navigate('/family-emergency-location');
  };

  // Função para buscar a lista de chamados do familiar
  const fetchChamados = async () => {
    try {
      const { url, familiarId } = validateEnvironment();
      const headers = getAuthHeaders();
      
      // Primeiro, tentar buscar chamados com notificacao_familiares
      let fetchUrl = `${url}/rest/v1/chamado?select=*&notificacao_familiares=cs.[{"id":"${familiarId}"}]&status=eq.A caminho`;
      let response = await fetch(fetchUrl, { method: 'GET', headers });
      let data = await response.json();
      
      // Se não encontrou chamados com notificacao_familiares, buscar por cliente_id
      if (!data || data.length === 0) {
        const clienteId = localStorage.getItem('clienteId');
        if (clienteId) {
          console.log('Buscando chamados por cliente_id:', clienteId);
          fetchUrl = `${url}/rest/v1/chamado?select=*&cliente_id=eq.${clienteId}&status=eq.A caminho`;
          response = await fetch(fetchUrl, { method: 'GET', headers });
          data = await response.json();
        }
      }
      
      if (!response.ok) {
        throw new Error(data.error_description || data.message || `Erro ${response.status}`);
      }
      
      data = data.sort((a: any, b: any) => {
        if (a.data_abertura && b.data_abertura) {
          return new Date(b.data_abertura).getTime() - new Date(a.data_abertura).getTime();
        }
        return (b.id || 0) - (a.id || 0);
      });
      
      // Verificar se há novos chamados
      const currentIds = data.map((c: any) => c.id);
      const previousIds = previousChamadosRef.current;
      
      // Se não é o carregamento inicial e há novos IDs
      if (previousIds.length > 0) {
        const newChamados = currentIds.filter((id: string) => !previousIds.includes(id));
        if (newChamados.length > 0) {
          console.log('Novos chamados detectados:', newChamados);
          ativarSinoNotificacao();
          // Iniciar o loop de notificação
          iniciarLoopNotificacao();
        }
      }
      
      // Atualizar a referência dos IDs anteriores
      previousChamadosRef.current = currentIds;
      
      setChamados(data);
    } catch (err) {
      setChamados([]);
      setError('Erro ao buscar chamados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('useEffect - Carregamento inicial executado');
    fetchChamados();

    // Cleanup function
    return () => {
      // Limpar intervalo ao desmontar o componente
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    console.log('useEffect - Realtime configurado');
    const subscription = supabase
      .channel('chamados-changes-familiar')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chamado'
        },
        (payload) => {
          console.log('Realtime detectou mudança:', payload.eventType, payload.table);
          
          // Verificar se a mudança é relevante para este familiar
          isRelevantChange(payload).then((isRelevant) => {
            if (isRelevant) {
              // Se é um INSERT de novo chamado
              if (payload.eventType === 'INSERT') {
                // Verificar se o status é "A caminho" antes de notificar
                if (payload.new?.status === 'A caminho') {
                  console.log('Novo chamado relevante com status "A caminho" detectado - ativando notificação');
                  ativarSinoNotificacao();
                  // Iniciar o loop de notificação
                  iniciarLoopNotificacao();
                }
              }
              // Se é UPDATE e o status mudou
              else if (payload.eventType === 'UPDATE' && payload.new) {
                const novoStatus = payload.new.status;
                const statusAnterior = payload.old?.status;
                
                // Notificar apenas se mudou PARA "A caminho" ou se já era "A caminho" e mudou para outro status válido
                if (novoStatus !== statusAnterior) {
                  if (novoStatus === 'A caminho') {
                    console.log('Status alterado para "A caminho" - ativando notificação');
                    ativarSinoNotificacao();
                    // Iniciar o loop de notificação
                    iniciarLoopNotificacao();
                  } else if (statusAnterior === 'A caminho' && 
                            novoStatus !== 'concluído' && 
                            novoStatus !== 'Pendente' && 
                            novoStatus !== 'finalizado') {
                    console.log('Status do chamado "A caminho" foi alterado - ativando notificação');
                    ativarSinoNotificacao();
                    // Iniciar o loop de notificação
                    iniciarLoopNotificacao();
                  }
                }
              }
              
              console.log('Mudança relevante detectada - recarregando lista');
              fetchChamados();
            } else {
              console.log('Mudança não relevante - ignorando');
            }
          }).catch((error) => {
            console.error('Erro ao verificar relevância:', error);
            // Em caso de erro, recarregar a lista por segurança
            fetchChamados();
          });
        }
      )
      .subscribe();
      
    return () => {
      console.log('useEffect - Realtime desconectado');
      subscription.unsubscribe();
      // Limpar intervalo ao desmontar o subscription
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current);
      }
    };
  }, []);

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedChamado(null);
  };

  return (
    <Box sx={{ background: 'transparent', boxShadow: 'none', border: 'none' }}>
      <audio ref={audioRef} src={`${process.env.PUBLIC_URL}/assets/notification.mp3`} />
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
          {chamados.map((c) => (
            <Paper key={c.id} sx={{ p: 2, mb: 1, boxShadow: 2 }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { sm: 'center' }, gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nº Chamado: {c.id}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, ml: { sm: 2 }, mt: { xs: 1, sm: 0 } }}>
                  {c.endereco_textual || 'Endereço não informado'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <span style={{ fontSize: 13 }}>{formatarDataHora(c.data_abertura)}</span>
                <span>
                  <Button
                    size="small"
                    color="primary"
                    onClick={() => handleVisualizar(c)}
                    disabled={c.status === 'finalizado' || c.status === 'concluído'}
                  >
                    Visualizar
                  </Button>
                </span>
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Função utilitária para formatar data/hora
function formatarDataHora(data: string) {
  if (!data) return '';
  const d = new Date(data);
  const dia = String(d.getDate()).padStart(2, '0');
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const ano = d.getFullYear();
  const hora = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${dia}/${mes}/${ano} ${hora}:${min}`;
}

export default NotificaoList;